import store from '@/store'
import useNotifications from '@/composables/useNotifications'
import { ref, computed } from '@vue/composition-api'
// import privilegesQuery from '@/@core/queries/privileges'
import i18n from '@/libs/i18n'
import { get, set, getMany, update } from 'idb-keyval';
import awsConnection from '@/views/habit/aws';
import useCommonTodo from '@/views/apps/todo/useCommonTodo'
import endpoints from '@/libs/endpoints'
import { useLambda } from '@/views/habit/awsLambda'
import { probabilityValues, impactValues, customHeatMapRiskMatrix, standardHeatMapRiskMatrix } from '@/constants'
import JSZip from 'jszip'
import { saveAs } from 'file-saver'
import useMongoDBFunctions from '@/views/habit/useMongoDBFunctions'
import { oid, date } from '@/helpers/mongoHelpers'

export default function useCommon() {
  const { role, worker_id, client, locations: userLocations } = store.state?.userStore?.userData
  const userLocationsQuery = userLocations?.map(e => ({ _id: e.value }))
  const { commitment_functionality } = JSON.parse(localStorage.getItem("clientData") || '{}')
  const { showErrorMessage } = useNotifications()
  const { singleUpload, getFile } = awsConnection()
  const { updateImprovementWithKey, sendImprovementEmail, uploadImprovementFile, uploadFileToS3 } = useCommonTodo()
  const { getItems, getItemsAggregate, createItem, updateItem, getPresignedDownloadUrl } = useLambda()
  const { processesQueryWithOffset, getLastCode } = useMongoDBFunctions()

  const mainQuery = {
    client_id: oid(client?.$oid),
    deleted: { $ne: true }
  }

  const mainQueryExtended = {
    ...mainQuery,
    onlyDropdownOptionn: { $ne: true }
  }

  const workerQuery = {
    client_id: oid(client?.$oid),
    onlyDropdownOption: { $ne: true }
  }

  const supervisorQuery = {
    ...workerQuery,
    privileges: { $ne: 'employee' },
  }

  // const varGeneral = {
  //   query: {
  //     client_id: { _id: client?.$oid },
  //     deleted_ne: true,
  //   }
  // }
  // const varGeneralExtended = {
  //   query: {
  //     client_id: { _id: client?.$oid },
  //     deleted_ne: true,
  //     onlyDropdownOption_ne: true
  //   }
  // }

  // const varWorkerTotal = {
  //   query: {
  //     client_id: { _id: client?.$oid },
  //     // deleted_ne: true,
  //     onlyDropdownOption_ne: true
  //   }
  // }

  // const varWorkerSupervised = {
  //   query: {
  //     client_id: { _id: client?.$oid },
  //     // deleted_ne: true,
  //     onlyDropdownOption_ne: true
  //   }
  // }

  // const varSupervisor = {
  //   query: {
  //     // deleted_ne: true,
  //     privileges_ne: 'employee',
  //     client_id: { _id: client?.$oid },
  //     onlyDropdownOption_ne: true
  //   }
  // }

  // if (role === 'supervisor' || role === 'internalAuditor' || role === 'externalAuditor') {
  //   varWorkerSupervised.query.supervisors_in = {_id: worker_id.$oid}
  //   delete varSupervisor.query.privileges_ne
  //   varSupervisor.query.privileges = 'supervisor'
  // }

  const roles = ref([])
  const workersTotal = ref([])
  const workersTotalNotMapped = ref([])
  const workersSupervised = ref([])
  const workersSupervisedNotMapped = ref([])
  const supervisors = ref([])
  const supervisorsOnlyNames = ref([])
  const locations = ref([])
  const crimes = ref([])
  const crimesData = ref([])
  const obligations = ref([])
  const workerPrivileges = ref([])
  const privileges = ref([])
  const processes = ref([])
  const motives = ref([])
  const metadata = ref([])
  const metadataNotMapped = ref([])
  const metadataFilterOptions = ref({zone: [], agency: []})
  const metadataTypeSelect = ref([])
  const metadataTypeSelectNotMapped = ref({})
  const improvements = ref([])
  const commitments = ref([])
  const companies = ref([])
  const ids = ref([])
  const responsibles = ref([])
  const controls = ref([])

  // Main IndexedDB/LocalStorage data keys that are essential for offline mode
  const storageDataKeys = [ "events", "locations", "processesComplete", "workersSupervised" ]

  const checkStorageData = () => {
    getMany(storageDataKeys)
      .then(values => {
        for (const value of values) {
          if (!value) {
            for (const key of storageDataKeys) {
              if (!localStorage[key]) {
                showErrorMessage(i18n.t('message.storage_data_not_detected'), false)
                break
              }
            }
            break
          }
        }
      })
  }

  const updateStorageData = () => {
    fetchAndStoreEvents()
    getRolesForDropDown()
    getWorkersTotalForDropDown("notMapped")
    getWorkersSupervisedForDropDown()
    getWorkersSupervisedForDropDown("notMapped")
    getSupervisorsForDropDown()
    getSupervisorsForDropDown("onlyNames")
    getLocationsForDropDown()
    getProcessesForDropDown()
    getMetadataForDropDown({ category: "improvement", option: "notMapped" })
    getMetadataForDropDown({ type: "select", option: "notMapped" })
    getMotivesForDropDown()
  }

  const storeDataToUpload = (storageProperty, payload) => {
    update(storageProperty, response => {
      if (response) {
        response.push({...payload})
        return response
      } else {
        return [{...payload}]
      }
    })
  }

  const handleError = ({error, defaultMessage, isStorageError}) => {
    if (isStorageError) return showErrorMessage(i18n.t('message.storage_data_fetch_error'))
    if (typeof error === 'string' && error.startsWith("timeout") || error?.message?.startsWith("timeout")) {
      return showErrorMessage(i18n.t('message.internet_connection_unstable'))
    }
    return showErrorMessage(defaultMessage)
  }

  const formatDateAsYMD = date => date.toISOString().split('T')[0]

  const formatDate = (date, hour = 12) => {
    let formattedDate;

    if (date instanceof Date || date.includes("T")) {
      // ISO 8601 format (e.g. 2025-01-17T15:00:00.000Z)
      formattedDate = new Date(date);
      formattedDate.setHours(hour, 0, 0, 0);
    } else if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      // yyyy-mm-dd format
      const [year, month, day] = date.split("-").map(Number);
      formattedDate = new Date(year, month - 1, day, hour, 0);
    } else {
      // dd-mm-yyyy or dd-mm-yyyy hh:mm
      const [datePart, timePart] = date.split(" ");
      const [day, month, year] = datePart.split("-").map(Number);
      const [hours, minutes] = timePart ? timePart.split(":").map(Number) : [hour, 0];
      formattedDate = new Date(year, month - 1, day, hours, minutes);
    }

    return formattedDate;
  };

  const getRolesForDropDown = async () => {
    get('roles').then(response => {
      if (response) roles.value = response
      else if (localStorage.roles) roles.value = JSON.parse(localStorage.roles)
    })

    try {
      const items = await getItems({ collection: 'role', query: mainQuery, options: { projection : { name: 1 }, sort: { name: 1 } } })

      roles.value = items?.map(({ name, _id }) => ({ title: name, value: _id.toString() })) || []
      set('roles', roles.value).then(() => {}).catch(error => console.log(error))
    } catch (error) {
      console.log(error)
      get('roles').then(response => {
        if (!response && !localStorage.roles) handleError({ isStorageError: true })
      })
    }
  }

  const getWorkersTotalForDropDown = async (option) => {
    if (option === "notMapped") {
      get('workersTotalNotMapped').then(response => {
        if (response) workersTotalNotMapped.value = response
        else if (localStorage.workersTotalNotMapped) workersTotalNotMapped.value = JSON.parse(localStorage.workersTotalNotMapped)
      })
    } else {
      get('workersTotal').then(response => {
        if (response) workersTotal.value = response
        else if (localStorage.workersTotal) workersTotal.value = JSON.parse(localStorage.workersTotal)
      })
    }

    try {
      const pipeline = [
        { $match: workerQuery },
        { $lookup: { from: 'location', localField: 'locations', foreignField: '_id', as: 'locations', pipeline: [ { $project: { location: 1 } }, { $addFields: { _id: { $toString: "$_id" } } } ] } },
        { $lookup: { from: 'worker', localField: 'supervisors', foreignField: '_id', as: 'supervisors', pipeline: [ { $project: { _id: 1 } }, { $addFields: { _id: { $toString: "$_id" } } } ] } },
        { $project: { name: 1, email: 1, supervisors: 1, locations: 1, onlyDropdownOption: 1, deleted: 1 } },
        { $sort: { name: 1 } },
        { $addFields: { _id: { $toString: "$_id" } } }
      ]

      const items = await getItemsAggregate({ collection: 'worker', pipeline })

      if (option === "notMapped") {
        workersTotalNotMapped.value = items || []
        set('workersTotalNotMapped', workersTotalNotMapped.value).then(() => {}).catch(error => console.log(error))
      } else {
        workersTotal.value = items?.map(({ name, _id, deleted, email }) => ({ title: name, value: _id, deleted, email })) || []
        set('workersTotal', workersTotal.value).then(() => {}).catch(error => console.log(error))
      }
    } catch (error) {
      console.log(error)
      if (option === "notMapped") {
        get('workersTotalNotMapped').then(response => {
          if (!response && !localStorage.workersTotalNotMapped) handleError({ isStorageError: true })
        })
      } else {
        get('workersTotal').then(response => {
          if (!response && !localStorage.workersTotal) handleError({ isStorageError: true })
        })
      }
    }
  }

  const getWorkersSupervisedForDropDown = async (option) => {
    if (option === "notMapped") {
      get('workersSupervisedNotMapped').then(response => {
        if (response) workersSupervisedNotMapped.value = response
        else if (localStorage.workersSupervisedNotMapped) workersSupervisedNotMapped.value = JSON.parse(localStorage.workersSupervisedNotMapped)
      })
    } else {
      get('workersSupervised').then(response => {
        if (response) workersSupervised.value = response
        else if (localStorage.workersSupervised) workersSupervised.value = JSON.parse(localStorage.workersSupervised)
      })
    }

    try {
      const pipeline = [
        { $match: workerQuery },
        { $lookup: { from: 'location', localField: 'locations', foreignField: '_id', as: 'locations', pipeline: [ { $project: { location: 1 } }, { $addFields: { _id: { $toString: "$_id" } } } ] } },
        { $lookup: { from: 'worker', localField: 'supervisors', foreignField: '_id', as: 'supervisors', pipeline: [ { $project: { _id: 1 } }, { $addFields: { _id: { $toString: "$_id" } } } ] } },
        { $project: { name: 1, email: 1, supervisors: 1, locations: 1, onlyDropdownOption: 1, deleted: 1 } },
        { $sort: { name: 1 } },
        { $addFields: { _id: { $toString: "$_id" } } }
      ]

      const items = await getItemsAggregate({ collection: 'worker', pipeline })

      if (option === "notMapped") {
        workersSupervisedNotMapped.value = items || []
        set('workersSupervisedNotMapped', workersSupervisedNotMapped.value).then(() => {}).catch(error => console.log(error))
      } else {
        workersSupervised.value = items?.map(({ name, _id, deleted }) => ({ title: name, value: _id, deleted })) || []
        set('workersSupervised', workersSupervised.value).then(() => {}).catch(error => console.log(error))
      }
    } catch (error) {
      console.log(error)
      if (option === "notMapped") {
        get('workersSupervisedNotMapped').then(response => {
          if (!response && !localStorage.workersSupervisedNotMapped) handleError({ isStorageError: true })
        })
      } else {
        get('workersSupervised').then(response => {
          if (!response && !localStorage.workersSupervised) handleError({ isStorageError: true })
        })
      }
    }
  }

  const getSupervisorsForDropDown = async (option) => {
    if (option === "onlyNames") {
      get('supervisorsOnlyNames').then(response => {
        if (response) supervisorsOnlyNames.value = response
        else if (localStorage.supervisorsOnlyNames) supervisorsOnlyNames.value = JSON.parse(localStorage.supervisorsOnlyNames)
      })
    } else {
      get('supervisors').then(response => {
        if (response) supervisors.value = response
        else if (localStorage.supervisors) supervisors.value = JSON.parse(localStorage.supervisors)
      })
    }

    try {
      const pipeline = [
        { $match: supervisorQuery },
        { $lookup: { from: 'location', localField: 'locations', foreignField: '_id', as: 'locations', pipeline: [ { $project: { location: 1 } }, { $addFields: { _id: { $toString: "$_id" } } } ] } },
        { $lookup: { from: 'worker', localField: 'supervisors', foreignField: '_id', as: 'supervisors', pipeline: [ { $project: { _id: 1 } }, { $addFields: { _id: { $toString: "$_id" } } } ] } },
        { $project: { name: 1, email: 1, supervisors: 1, locations: 1, onlyDropdownOption: 1, deleted: 1 } },
        { $sort: { name: 1 } },
        { $addFields: { _id: { $toString: "$_id" } } }
      ]

      const items = await getItemsAggregate({ collection: 'worker', pipeline })

      if (option === "onlyNames") {
        supervisorsOnlyNames.value = Array.from(new Set(items?.map(({ name }) => name) || []))
        set('supervisorsOnlyNames', supervisorsOnlyNames.value).then(() => {}).catch(error => console.log(error))
      } else {
        supervisors.value = items?.map(({ name, _id, locations, deleted }) => ({ title: name, value: _id, locationIds: locations.map(e => e._id), locationNames: locations.map(e => e.location), deleted })) || []
        set('supervisors', supervisors.value).then(() => {}).catch(error => console.log(error))
      }
    } catch (error) {
      console.log(error)
      if (option === "onlyNames") {
        get('supervisorsOnlyNames').then(response => {
          if (!response && !localStorage.supervisorsOnlyNames) handleError({ isStorageError: true })
        })
      } else {
        get('supervisors').then(response => {
          if (!response && !localStorage.supervisors) handleError({ isStorageError: true })
        })
      }
    }
  }

  const getLocationsForDropDown = async () => {
    get('locations').then(response => {
      if (response) locations.value = response
      else if (localStorage.locations) locations.value = JSON.parse(localStorage.locations)
    })

    try {
      const items = await getItems({ collection: 'location', query: mainQueryExtended, options: { projection : { location: 1 }, sort: { location: 1 } } })

      locations.value = items?.map(({ location, _id }) => ({ title: location, value: _id.toString() })) || []
      set('locations', locations.value).then(() => {}).catch(error => console.log(error))
    } catch (error) {
      console.log(error)
      get('locations').then(response => {
        if (!response && !localStorage.locations) handleError({ isStorageError: true })
      })
    }
  }

  const getImprovementsForDropDown = async () => {
    get('improvements').then(response => {
      if (response) improvements.value = response
    })

    try {
      const items = await getItems({ collection: 'improvement', query: mainQuery, options: { projection : { note: 1 }, sort: { note: 1 } } })

      improvements.value = items?.map(({ note, _id }) => ({ title: note, value: _id.toString() })) || []
      set('improvements', improvements.value).then(() => {}).catch(error => console.log(error))
    } catch (error) {
      console.log(error)
      get('improvements').then(response => {
        if (!response) handleError({ isStorageError: true })
      })
    }
  }

  const getCommitmentsForDropDown = async () => {
    get('commitments').then(response => {
      if (response) commitments.value = response
    })

    try {
      const items = await getItems({ collection: 'commitment', query: mainQuery, options: { projection : { title: 1 }, sort: { title: 1 } } })

      commitments.value = items?.map(({ title, _id }) => ({ title, value: _id.toString() })) || []
      set('commitments', commitments.value).then(() => {}).catch(error => console.log(error))
    } catch (error) {
      console.log(error)
      get('commitments').then(response => {
        if (!response) handleError({ isStorageError: true })
      })
    }
  }

  const getProcessesForDropDown = async () => {
    get('processesDropdown').then(response => {
      if (response) processes.value = response
      else if (localStorage.processesDropdown) processes.value = JSON.parse(localStorage.processesDropdown)
    })

    try {
      const input = {
        limit: 10000,
        sortBy: 'name',
        client_id: client.$oid,
        worker_id: worker_id.$oid,
        role,
      }

      const items = await processesQueryWithOffset(input)

      processes.value = items?.processes?.map(e => {
        if (e.metadataData) {
            // This variable cannot come as 'metadata' from the backend because it interferes with the process schema validation
            e.metadata = e.metadataData
            delete e.metadataData
          }
          if (e.code) e.name = `${e.code} / ${e.name}`
          return { title: e.name, value: e._id.toString(), evaluated: e.evaluated }
      }) || []

      // Sorts the processes by code and then by name
      processes.value = sortProcesses(processes.value)

      set('processesComplete', items?.processes || []).then(() => {}).catch(error => console.log(error))
      set('processesDropdown', processes.value).then(() => {}).catch(error => console.log(error))
    } catch (error) {
      console.log(error)
      get('processesDropdown').then(response => {
        if (!response && !localStorage.processesDropdown) handleError({ isStorageError: true })
      })
    }
  }

  const sortProcesses = (processesArray) => {
    // Regular expression to capture letters, numbers, and the remaining text
    const regex = /^([A-Za-z]*)(\d*)\s*\/?\s*(.*)$/;
  
    return processesArray.sort((a, b) => {
      // Match the title of each process against the regex
      const matchA = a.title.match(regex)
      const matchB = b.title.match(regex)
  
      // Extract the letters, numbers, and text from the match for both processes
      const lettersA = matchA[1] || ""  // If no letters are found, default to an empty string
      const numbersA = matchA[2] ? parseInt(matchA[2], 10) : 0  // If no numbers are found, default to 0
      const textA = matchA[3].trim().toLowerCase() || a.title.toLowerCase()  // Default to the title if no extra text is found
  
      const lettersB = matchB[1] || ""  // Same for process B
      const numbersB = matchB[2] ? parseInt(matchB[2], 10) : 0
      const textB = matchB[3].trim().toLowerCase() || b.title.toLowerCase()
  
      // First, compare letters (alphabetically), considering numbers in the letter part
      const lettersComparison = lettersA.localeCompare(lettersB, 'es', { numeric: true })
      if (lettersComparison !== 0) return lettersComparison
  
      // If letters are the same, compare by numbers
      if (numbersA !== numbersB) return numbersA - numbersB
  
      // Finally, compare the remaining text
      return textA.localeCompare(textB, 'es', { numeric: true })
    })
  }

  const getMotivesForDropDown = async () => {
    get('motives').then(response => {
      if (response) motives.value = response
      else if (localStorage.motives) motives.value = JSON.parse(localStorage.motives)
    })

    try {
      const items = await getItems({ collection: 'motive', query: mainQuery, options: { projection : { value: 1, label: 1 }, sort: { label: 1 } } })
      items?.forEach(e => e._id = e._id.toString())

      motives.value = items || []
      set('motives', motives.value).then(() => {}).catch(error => console.log(error))
    } catch (error) {
      console.log(error)
      get('motives').then(response => {
        if (!response && !localStorage.motives) handleError({ isStorageError: true })
      })
    }
  }
  
  const getMetadataForDropDown = async ({category, option, type}) => {
    if (category === "filter") {
      get('metadataFilterOptions').then(response => {
        if (response) {
          response.forEach(e => {
            Object.assign(metadataFilterOptions.value, {[e.name]: e.options})
          })
        } else if (localStorage.metadataFilterOptions) {
          JSON.parse(localStorage.metadataFilterOptions).forEach(e => {
            Object.assign(metadataFilterOptions.value, {[e.name]: e.options})
          })
        }
      })
    }
    else if (option === "notMapped") {
      if (type === "select") {
        get('metadataTypeSelectNotMapped').then(response => {
          if (response) metadataTypeSelectNotMapped.value = response
          else if (localStorage.metadataTypeSelectNotMapped) metadataTypeSelectNotMapped.value = JSON.parse(localStorage.metadataTypeSelectNotMapped)
        })
      } else {
        get('metadataNotMapped').then(response => {
          if (response) metadataNotMapped.value = response
          else if (localStorage.metadataNotMapped) metadataNotMapped.value = JSON.parse(localStorage.metadataNotMapped)
        })
      }
    }
    else if (type === "select") {
      get('metadataTypeSelect').then(response => {
        if (response) metadataTypeSelect.value = response
        else if (localStorage.metadataTypeSelect) metadataTypeSelect.value = JSON.parse(localStorage.metadataTypeSelect)
      })
    }
    else {
      get('metadata').then(response => {
        if (response) metadata.value = response
        else if (localStorage.metadata) metadata.value = JSON.parse(localStorage.metadata)
      })
    }

    try {
      const reformattedQuery = { ...mainQuery }
      if (category) reformattedQuery.categories = category
      if (type) reformattedQuery.type = type

      const items = await getItems({ collection: 'metadata', query: reformattedQuery, options: { projection : { name: 1, type: 1, options: 1 }, sort: { name: 1 } } })
      items?.forEach(e => e._id = e._id.toString())

      if (category === "filter") {
        items.forEach(e => {
          Object.assign(metadataFilterOptions.value, {[e.name]: e.options})
        })
        set('metadataFilterOptions', items).then(() => {}).catch(error => console.log(error))
      }
      else if (option === "notMapped") {
        if (type === "select") {
          items?.forEach(e => {
            Object.assign(metadataTypeSelectNotMapped.value, {[e.name]: e.options})
          })
          set('metadataTypeSelectNotMapped', metadataTypeSelectNotMapped.value).then(() => {}).catch(error => console.log(error))
        } else {
          metadataNotMapped.value = items || []
          set('metadataNotMapped', items).then(() => {}).catch(error => console.log(error))
        }
      }
      else {
        const metadataResponseMapped = computed(() => {
          return items?.map(({ name, _id }) => ({ title: i18n.t(`metadata.${name}`), value: _id }))
        })
        if (type === "select") {
          metadataTypeSelect.value = metadataResponseMapped
          set('metadataTypeSelect', metadataResponseMapped).then(() => {}).catch(error => console.log(error))
        }
        else {
          metadata.value = metadataResponseMapped
          set('metadata', metadataResponseMapped).then(() => {}).catch(error => console.log(error))
        }
      }
    } catch (error) {
      console.log(error)
      if (category === "filter") {
        get('metadataFilterOptions').then(response => {
          if (!response && !localStorage.metadataFilterOptions) handleError({ isStorageError: true })
        })
      }
      else if (option === "notMapped") {
        if (type === "select") {
          get('metadataTypeSelectNotMapped').then(response => {
            if (!response && !localStorage.metadataTypeSelectNotMapped) handleError({ isStorageError: true })
          })
        }
        get('metadataNotMapped').then(response => {
          if (!response && !localStorage.metadataNotMapped) handleError({ isStorageError: true })
        })
      }
      else if (type === "select") {
        get('metadataTypeSelect').then(response => {
          if (!response && !localStorage.metadataTypeSelect) handleError({ isStorageError: true })
        })
      }
      else {
        get('metadata').then(response => {
          if (!response && !localStorage.metadata) handleError({ isStorageError: true })
        })
      }
    }
  }

  const fetchAndStoreEvents = async () => {
    const calendars = ["Futuras", "Vencidas"]

    const now = new Date()
    const firstDayOfPreviousMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1)
    const firstDayOfNextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1)

    try {
      const initialQuery = {
        ...mainQuery,
        'extendedProps.calendar': { $in: calendars },
        start: { $gte: date(firstDayOfPreviousMonth), $lt: date(firstDayOfNextMonth) },
      }

      const finalQuery = {
        $or: [{ "attendee.deleted": { $ne: true } }, { attendee: { $exists: false } }]
      }

      if (role === 'supervisor') {
        finalQuery.$or[0]["attendee.supervisors"] = oid(worker_id.$oid)
        finalQuery.$or[1]["organizer.locations"] = { $in: userLocationsQuery.map(e => oid(e._id)) }
        finalQuery.$or[2] = { "attendee._id": worker_id.$oid }
      }

      const pipeline = [
        { $match: initialQuery },
        { $lookup: { from: 'worker', localField: 'attendee', foreignField: '_id', as: 'attendee', pipeline: [ { $project: { deleted: 1, supervisors: 1, name: 1 } }, { $addFields: { _id: { $toString: "$_id" } } } ] } },
        { $lookup: { from: 'worker', localField: 'organizer', foreignField: '_id', as: 'organizer', pipeline: [ { $project: { locations: 1 } } ] } },
        { $addFields: { attendee: { $arrayElemAt: ["$attendee", 0] }, organizer: { $arrayElemAt: ["$organizer", 0] } } },
        { $match: finalQuery },
        { $lookup: { from: 'process', localField: 'process', foreignField: '_id', as: 'process', pipeline: [ { $project: { name: 1 } }, { $addFields: { _id: { $toString: "$_id" } } } ] } },
        { $lookup: { from: 'confirmation', localField: 'confirmation', foreignField: '_id', as: 'confirmation', pipeline: [ { $project: { pending: 1, supervisor: 1 } }, { $addFields: { _id: { $toString: "$_id" } } }, { $lookup: { from: 'worker', localField: 'supervisor', foreignField: '_id', as: 'supervisor', pipeline: [ { $project: { name: 1 } }, { $addFields: { _id: { $toString: "$_id" } } } ] } }, { $addFields: { supervisor: { $arrayElemAt: ["$supervisor", 0] } } } ] } },
        { $lookup: { from: 'commitment', localField: 'commitment', foreignField: '_id', as: 'commitment', pipeline: [ { $project: { completed: 1 } }, { $addFields: { _id: { $toString: "$_id" } } } ] } },
        { $lookup: { from: 'improvement', localField: 'improvements', foreignField: '_id', as: 'improvements', pipeline: [ { $addFields: { _id: { $toString: "$_id" }, assistance: { $toString: "$assistance" }, dueDate: { $cond: [{ $eq: [{ $type: "$dueDate" }, "string"] }, "$dueDate", { $dateToString: { format: "%Y-%m-%dT%H:%M:%SZ", date: "$dueDate" } }] } } }, { $lookup: { from: 'worker', localField: 'assignee', foreignField: '_id', as: 'assignee', pipeline: [ { $project: { name: 1, email: 1 } }, { $addFields: { _id: { $toString: "$_id" } } } ] } }, { $lookup: { from: 'worker', localField: 'subscribers', foreignField: '_id', as: 'subscribers', pipeline: [ { $project: { _id: 1 } }, { $addFields: { _id: { $toString: "$_id" } } } ] } }, { $addFields: { assignee: { $arrayElemAt: ["$assignee", 0] } } } ] } },
        { $lookup: { from: 'worker', localField: 'participants', foreignField: '_id', as: 'participants', pipeline: [ { $project: { _id: 1 } }, { $addFields: { _id: { $toString: "$_id" } } } ] } },
        { $addFields: { _id: { $toString: "$_id" }, assistance: { $toString: "$assistance" }, process: { $arrayElemAt: ["$process", 0] }, confirmation: { $arrayElemAt: ["$confirmation", 0] }, commitment: { $arrayElemAt: ["$commitment", 0] }, start: { $cond: [{ $eq: [{ $type: "$start" }, "string"] }, "$start", { $dateToString: { format: "%Y-%m-%dT%H:%M:%SZ", date: "$start" } }] }, end: { $cond: [{ $eq: [{ $type: "$end" }, "string"] }, "$end", { $dateToString: { format: "%Y-%m-%dT%H:%M:%SZ", date: "$end" } }] }, modifiedAt: { $cond: [{ $eq: [{ $type: "$modifiedAt" }, "string"] }, "$modifiedAt", { $dateToString: { format: "%Y-%m-%dT%H:%M:%SZ", date: "$modifiedAt" } }] } } },
      ]

      const items = await getItemsAggregate({ collection: 'event', pipeline })

      if (!Array.isArray(items)) {
        console.error('fetchAndStoreEvents response is not an array:', items)
        return
      }

      const events = items || []
      set('events', events).then(() => {}).catch(error => console.log(error))
    } catch (error) {
      console.log(error)
    }
  }

  const updateConfirmationWithKey = async (confirmationId, key) => {
    try {
      const query = { _id: oid(confirmationId) }
      const action = { $set: { imageKey: key } }

      await updateItem({ collection: 'confirmation', query, action })
    } catch (error) {
      console.log(error)
      showErrorMessage(i18n.t('message.confirmation_update_error'))
    }
  }

  const createImprovements = async (improvements, origin, originId) => {
    if (!improvements?.length) return

    const improvementCreationPromises = improvements.map(i => {
      const newImprovement = {
        client_id: oid(i.client_id),
        assignee: i.assignee ? oid(i.assignee._id) : null,
        note: i.note,
        tags: i.tags,
        description: i.description,
        dueDate: i.dueDate ? date(new Date(`${i.dueDate.slice(0, 10)} 12:00:00`)) : null,
        important: i.important,
        origin,
        origin_id: oid(originId),
      }
      if (i.metadata?.length) {
        newImprovement.metadata = i.metadata.map(m => ({
          name: m.name,
          type: m.type,
          answer: m.answer,
        }))
      }
      // if (i.associatedCase) {
      //   newImprovement.associatedCase = { oid(i.associatedCase) }
      // }

      return new Promise(async (resolve, reject) => {
        try {    
          const { insertedId } = await createItem({ collection: 'improvement', payload: newImprovement })
          const newImprovementId = insertedId.toString()

          if (i.documentFile?.name) {
            const destinationFolder = `${client.$oid}/documents`
            uploadFileToS3(i.documentFile, destinationFolder)
              .then((key) => updateImprovementWithKey(newImprovementId, key))
              .catch((err) => console.log(err))
          }

          if (i.assignee?.email) sendImprovementEmail(i)

          resolve(newImprovementId)
        } catch (error) {
          console.log(error)
          showErrorMessage(commitment_functionality ? i18n.t('message.commitment_error') : i18n.t('message.improvement_error'))
          resolve(null)
        }
      })
    })

    const ids = await Promise.all(improvementCreationPromises)

    return ids
  }

  const createCommitment = async (commitment) => {
    const documentFile = commitment.documentFile
    delete commitment.documentFile

    try {    
      const { insertedId } = await createItem({ collection: 'commitment', payload: commitment })
      
      // Upload document to AWS and then update the commitment in MongoDB with the AWS document key
      if (documentFile?.name) {
        uploadCommitmentFile(documentFile, "documents", insertedId.toString())
      }

      createBehaviourEvaluationEvent(commitment, insertedId.toString())
    } catch (error) {
      console.log(error)
      showErrorMessage(i18n.t('message.commitment_error'))
    }
  }

  const createCase = async (caseData) => {
    const documentFile = caseData.documentFile
    delete caseData.documentFile

    const query = {
      collection: 'improvement',
      client_id: client.$oid
    }
    const lastCode = await getLastCode(query)
    caseData.code = lastCode + 1

    try {    
      const { insertedId } = await createItem({ collection: 'improvement', payload: caseData })
      
      // Upload document to AWS and then update the commitment in MongoDB with the AWS document key
      if (documentFile?.length) {
        uploadImprovementFile(documentFile, "documents", insertedId.toString())
      }
    } catch (error) {
      console.log(error)
      showErrorMessage(i18n.t('message.commitment_error'))
    }
  }

  const uploadCommitmentFile = (file, folder, commitmentId) => {
    const destinationFolder = `${client.$oid}/${folder}`
    uploadFileToS3(file, destinationFolder)
      .then((key) => updateCommitmentWithKey(commitmentId, key))
      .catch((err) => console.log(err))
  }

  const updateCommitmentWithKey = async (commitmentId, key) => {
    try {
      const query = { _id: oid(commitmentId) }
      const action = { $set: { imageKey: key } }

      await updateItem({ collection: 'commitment', query, action })
    } catch (error) {
      console.log(error)
      showErrorMessage(i18n.t('message.improvement_update_error'))
    }
  }

  const createBehaviourEvaluationEvent = async (commitment, newCommitmentId) => {
    const endDate = new Date(commitment.dueDate)
    endDate.setHours(13, 0, 0, 0)

    const payload = {
      client_id: oid(commitment.client_id),
      attendee: oid(commitment.assignee),
      commitment: oid(newCommitmentId),
      title: commitment.title,
      start: date(commitment.dueDate),
      end: date(endDate),
      extendedProps: { calendar: 'Futuras' },
      isBehaviourEvaluation: true
    }

    try {    
      await createItem({ collection: 'event', payload })
    } catch (error) {
      console.log(error)
      showErrorMessage(i18n.t('message.event_create_error'))
    }
  }

  const createActionPlanEvent = (actionPlan) => {
    const startDate = actionPlan.type === 'immediate' ? actionPlan.startDate : actionPlan.endDate
    const endDate = new Date(startDate)
    endDate.setHours(13, 0, 0, 0)

    const payload = {
      client_id: oid(client.$oid),
      attendee: actionPlan.executionResponsible || null,
      actionPlan: oid(actionPlan._id),
      title: actionPlan.name,
      start: date(startDate),
      end: date(endDate),
      extendedProps: { calendar: actionPlan.type === 'immediate' ? 'Realizadas' : 'Futuras' },
      isActionPlan: true
    }

    try {
      createItem({ collection: 'event', payload })
    } catch (error) {
      console.log(error)
      showErrorMessage(i18n.t('message.event_create_error'))
    }
  }

  const updateActionPlanEvent = (actionPlanId, executionDate) => {
    const startDate = executionDate
    const endDate = new Date(startDate)
    endDate.setHours(13, 0, 0, 0)

    const query = { actionPlan: oid(actionPlanId) }

    const payload = {
      extendedProps: { calendar: "Realizadas" },
      start: date(startDate),
      end: date(endDate),
    }

    const action = { $set: payload }

    try {
      updateItem({ collection: 'event', query, action })
    } catch (error) {
      console.log(error)
      showErrorMessage(i18n.t('message.event_update_error'))
    }
  }

  const getFileExtension = (key) => {
    return key?.split('?')[0].split('.').pop()?.toLowerCase()
  }

  const canPreviewDirectly = (key) => {
    if (!key) return false

    const ext = getFileExtension(key)

    return [ 'pdf', 'jpg', 'jpeg', 'png', 'gif', 'webp', 'txt' ].includes(ext)
  }

  const needsOfficeViewer = (key) => {
    if (!key) return false
    
    const ext = getFileExtension(key)

    return [ 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'].includes(ext)
  }

  const canBeViewed = (key) => {
    if (!key) return false
    
    return canPreviewDirectly(key) || needsOfficeViewer(key)
  }

  const openLocalFile = (file) => {
    const url = URL.createObjectURL(file)

    window.open(url, '_blank')

    // liberar memoria después
    setTimeout(() => URL.revokeObjectURL(url), 10000)
  }

  const downloadFile = async ({ key, url, file }) => {
    let href
    let blob

    if (url) {
      const response = await fetch(url)
      blob = await response.blob()
  
      href = window.URL.createObjectURL(blob)
    } else {
      href = URL.createObjectURL(file)
    }

    const link = document.createElement('a')
    link.href = href
    link.download = key ? getCleanFileName(key) : file.name

    document.body.appendChild(link)
    link.click()
    link.remove()

    window.URL.revokeObjectURL(url ? blob : file)
  }

  const openOfficeViewer = (url) => {
    const fileUrl = encodeURIComponent(url)

    window.open(`https://view.officeapps.live.com/op/view.aspx?src=${fileUrl}`)
  }

  const openDocument = async (key, isDownload = false) => {
    if (!key) {
      showErrorMessage(i18n.t('message.document_not_available'))
      return
    }

    const openOne = async (k) => {
      const { url } = await getPresignedDownloadUrl({ key: k })

      if (isDownload) {
        return downloadFile({ key: k, url })
      }

      if (canPreviewDirectly(k)) {
        window.open(url, '_blank')
        return
      }

      if (needsOfficeViewer(k)) {
        openOfficeViewer(url)
        return
      }

      // fallback → descargar
      downloadFile({ key: k, url })
    }

    if (Array.isArray(key)) {
      for (const k of key) {
        openOne(k)
        await new Promise(r => setTimeout(r, 300))
      }
    } else {
      await openOne(key)
    }
  }

  const getCleanFileName = (key) => {
    const cleanName = key?.split('/')?.pop()?.replace(/^\d+-/, '') || 'File'

    return cleanName
  }

  const downloadZip = async (keys, type = 'evidence') => {
    const zip = new JSZip()
    const zipFileName = `${i18n.t(type === 'evidence' ? 'Evidences' : 'Documents')}.zip`

    await Promise.all(keys.map(async (key) => {
      const { url } = await getPresignedDownloadUrl({ key })

      const response = await fetch(url)
      const blob = await response.blob()

      const fileName = getCleanFileName(key)

      zip.file(fileName, blob)
    }))

    const content = await zip.generateAsync({ type: 'blob' })

    saveAs(content, zipFileName)
  }

  const openImage = async (imageKey) => {
    let w = window.open("");
    w.document.write(`<h2>${i18n.t('message.loading_image')}</h2>`);
    try {
      const imgFromAWS = await getFile(endpoints.aws_bucket, imageKey)
      let image = new Image();
      image.src = imgFromAWS;

      w.document.body.innerHTML = "";
      w.document.write(image.outerHTML);
    } catch (error) {
      console.log(error)
      showErrorMessage(i18n.t('message.err_open_image'))
    }
  }

  const getCrimesForDropDown = async () => {
    get('crimes').then(response => {
      if (response) crimes.value = response
      else if (localStorage.crimes) crimes.value = JSON.parse(localStorage.crimes)
    })

    try {
      const items = await getItems({ collection: 'crime', query: mainQuery, options: { projection : { name: 1, description: 1, lawNumber: 1, law: 1, article: 1, number: 1 }, sort: { name: 1 } } })

      crimesData.value = items || []

      crimes.value = items?.map(({ name, _id, law, lawNumber, article }) => {
          if (lawNumber) name += ` / ${lawNumber}`
          if (law) name += ` / ${law}`
          if (article) name += ` / ${article}`
          return { title: name, value: _id.toString() }
        }) || []

      set('crimes', crimes.value).then(() => {}).catch(error => console.log(error))
    } catch (error) {
      console.log(error)
      get('crimes').then(response => {
        if (!response && !localStorage.crimes) handleError({ isStorageError: true })
      })
    }
  }

  // const getPrivilegesForDropDown = () => {
  //   if (localStorage.privileges) privileges.value = JSON.parse(localStorage.privileges)
  //   store
  //     .dispatch('processStoreModule/getPrivilegesForDropDown', { query: privilegesQuery, variables: { ...varGeneral, limit: 20000, sortBy: "NAME_ASC" } })
  //     .then(response => {
  //       if (response.data.errors) throw new Error(response.data.errors[0].message)
  //       privileges.value = response.data?.data?.privileges?.map(({ name, value }) => ({ title: name, value })) || []
  //       localStorage.privileges = JSON.stringify(privileges.value)
  //     })
  //     .catch((error) => {
  //       console.log(error)
  //       if (!localStorage.privileges) handleError({ isStorageError: true })
  //     })
  // }

  function getPrivilegesForWorker() {
    let defaultPrivilege = []
    if (role === 'supervisor') {
      defaultPrivilege = [{ value: 'employee', title: i18n.t('userRoles.Employee') }]
    } else {
      defaultPrivilege = [
        { value: 'consultant', title: i18n.t('userRoles.Consultant') },
        // { value: 'internalAuditor', title: i18n.t('userRoles.InternalAuditor') },
        // { value: 'externalAuditor', title: i18n.t('userRoles.ExternalAuditor') },
        { value: 'manager', title: i18n.t('userRoles.Manager') },
        { value: 'supervisor', title: i18n.t('userRoles.Supervisor') },
        { value: 'employee', title: i18n.t('userRoles.Employee') },
      ]
    }
    workerPrivileges.value = defaultPrivilege
  }

  const setState = (c, startOfToday) => {
    switch (c.stage) {
      case "end":
        return "completed";

      case "registration":
        return (c.dueDate && new Date(c.dueDate) < startOfToday) ? "delayed" : "upToDate";

      case "analysis":
        return (c.proposalDueDate && new Date(c.proposalDueDate) < startOfToday) ? "delayed" : "upToDate";

      case "approval":
        return (c.approvalDueDate && new Date(c.approvalDueDate) < startOfToday) ? "delayed" : "upToDate";

      case "corrective": {
        const anyDelayed = c.actionPlans?.some(plan =>
          plan.endDate ? new Date(plan.endDate) <= startOfToday : false
        );
        return anyDelayed ? "delayed" : "upToDate";
      }

      case "verification":
        return (c.verificationDueDate && new Date(c.verificationDueDate) < startOfToday) ? "delayed" : "upToDate";

      default:
        return "upToDate";
    }
  }

  const getRiskValue = (probability, impact) => {
    if (!probability || !impact) return null

    const { customHeatMap } = JSON.parse(localStorage.getItem('clientData') || '{}')
    const probabilityValue = probabilityValues[probability]
    const impactValue = impactValues[impact]

    if (customHeatMap) {
      // Use custom heat map matrix for LV client
      const riskCategory = customHeatMapRiskMatrix[impactValue]?.[probabilityValue] || 'medium'
      return { value: riskCategory, title: i18n.t(`residualRisk.${riskCategory}`) }
    } else {
      // Use standard calculation for regular clients
      const riskCategory = standardHeatMapRiskMatrix[impactValue]?.[probabilityValue] || 'medium'
      return { value: riskCategory, title: i18n.t(`residualRisk.${riskCategory}`) }
    }
  }

  /**
   * Convierte un valor numérico de RR (1-25) a su categoría textual según la matriz de riesgo
   * @param {number} numericRR - Valor numérico del RR (1-25)
   * @returns {string} - Categoría del riesgo (veryLow, low, medium, high, veryHigh)
   */
  const getRiskCategoryFromNumeric = (numericRR) => {
    if (!numericRR || numericRR < 1 || numericRR > 25) return 'medium'

    const { customHeatMap } = JSON.parse(localStorage.getItem('clientData') || '{}')
    const matrix = customHeatMap ? customHeatMapRiskMatrix : standardHeatMapRiskMatrix

    // Buscar en la matriz qué combinación de probabilidad × impacto da ese valor numérico
    // Probabilidad: 1-5, Impacto: 1-5
    for (let impact = 1; impact <= 5; impact++) {
      for (let probability = 1; probability <= 5; probability++) {
        if (impact * probability === numericRR) {
          return matrix[impact][probability]
        }
      }
    }

    // Si no hay coincidencia exacta, usar rangos aproximados
    if (numericRR <= 2) return 'veryLow'
    if (numericRR <= 4) return 'low'
    if (numericRR <= 9) return 'medium'
    if (numericRR <= 16) return 'high'
    return 'veryHigh'
  }

  /**
   * Calcula el nuevo RR aplicando un factor multiplicativo según el nivel de cumplimiento
   * @param {number} currentRR - RR actual del proceso (1-25)
   * @param {number} compliancePercentage - Porcentaje promedio de cumplimiento (0-100)
   * @returns {object} - { newRR: número del nuevo RR, level: nivel de resultado, factor: factor aplicado }
   */
  const calculateNewRR = (currentRR, compliancePercentage) => {
    if (!currentRR || compliancePercentage === null || compliancePercentage === undefined) {
      return null
    }

    // Determinar nivel y factor según porcentaje de cumplimiento
    let level, factor
    if (compliancePercentage >= 76) {
      level = 'optimal'
      factor = -0.25
    } else if (compliancePercentage >= 51) {
      level = 'good'
      factor = -0.10
    } else if (compliancePercentage >= 26) {
      level = 'insufficient'
      factor = 0.20
    } else {
      level = 'critical'
      factor = 0.45
    }

    // Calcular nuevo RR teórico
    const theoreticalRR = currentRR * (1 + factor)

    // Redondear: >=0.5 sube, <0.5 baja
    let newRR = Math.round(theoreticalRR)

    // Aplicar límites 1-25
    if (newRR < 1) newRR = 1
    if (newRR > 25) newRR = 25

    return {
      newRR,
      level,
      factor,
      theoreticalRR
    }
  }

  const getBusinessDays = (start, end) => {
    if (!start || !end) return null

    let count = 0
    const current = formatDate(start)
    const last = formatDate(end)

    // Normalizar horas
    current.setHours(0,0,0,0)
    last.setHours(0,0,0,0)

    while (current < last) {
      const day = current.getDay()
      if (day !== 0 && day !== 6) count++ // 0 domingo, 6 sábado
      current.setDate(current.getDate() + 1)
    }

    return count
  }

  const getCompaniesForDropDown = async () => {
    try {
      const items = await getItems({ collection: 'company', query: mainQuery, options: { projection : { name: 1 }, sort: { name: 1 } } })

      companies.value = items?.map(({ name, _id }) => ({ title: name, value: _id.toString() })) || []
    } catch (error) {
      console.log(error)
    }
  }

  const getIDsForDropDown = async () => {
    try {
      const items = await getItems({ collection: 'id', query: mainQuery, options: { projection : { name: 1 }, sort: { name: 1 } } })

      ids.value = items?.map(({ name, _id }) => ({ title: name, value: _id.toString() })) || []
    } catch (error) {
      console.log(error)
    }
  }

  const getResponsiblesForDropDown = async () => {
    try {
      // Get unique responsible values from control collection (preferring responsible_id, falling back to the old free-text responsible)
      const pipeline = [
        { $match: mainQuery },
        { $lookup: { from: 'worker', localField: 'responsible_id', foreignField: '_id', as: 'responsibleWorkerData', pipeline: [ { $project: { name: 1 } } ] } },
        { $addFields: { responsibleDisplay: { $ifNull: [ { $arrayElemAt: ["$responsibleWorkerData.name", 0] }, "$responsible" ] } } },
        { $match: { responsibleDisplay: { $exists: true, $ne: '' } } },
        { $group: { _id: '$responsibleDisplay' } },
        { $sort: { _id: 1 } }
      ]

      const items = await getItemsAggregate({ collection: 'control', pipeline })

      responsibles.value = items?.map(item => ({ title: item._id, value: item._id })) || []
    } catch (error) {
      console.log(error)
    }
  }

  const getControlsForDropDown = async () => {
    try {
      const items = await getItems({ collection: 'control', query: mainQuery, options: { projection: { name: 1 }, sort: { name: 1 } } })

      controls.value = items?.map(({ name, _id }) => ({ title: name, value: _id.toString() })) || []
    } catch (error) {
      console.log(error)
    }
  }

  const getGapObligationsForDropDown = async () => {
    try {
      const items = await getItems({
        collection: 'gapObligation',
        query: { deleted: { $ne: true } },
        options: { projection: { name: 1, article: 1, order: 1 }, sort: { order: 1 } }
      })

      obligations.value = items?.map(({ name, article, order, _id }) => ({
        title: `${order}. ${name} (${article})`,
        value: _id.toString()
      })) || []
    } catch (error) {
      console.log(error)
    }
  }

  return {
    getRolesForDropDown,
    roles,
    getWorkersTotalForDropDown,
    workersTotal,
    workersTotalNotMapped,
    getWorkersSupervisedForDropDown,
    workersSupervised,
    workersSupervisedNotMapped,
    getSupervisorsForDropDown,
    supervisors,
    supervisorsOnlyNames,
    // getPrivilegesForDropDown,
    privileges,
    getProcessesForDropDown,
    processes,
    getLocationsForDropDown,
    locations,
    getCrimesForDropDown,
    crimes,
    crimesData,
    getPrivilegesForWorker,
    workerPrivileges,
    getMotivesForDropDown,
    motives,
    getMetadataForDropDown,
    metadata,
    metadataNotMapped,
    metadataFilterOptions,
    metadataTypeSelect,
    metadataTypeSelectNotMapped,
    handleError,
    checkStorageData,
    updateStorageData,
    fetchAndStoreEvents,
    storeDataToUpload,
    formatDate,
    formatDateAsYMD,
    createCommitment,
    updateConfirmationWithKey,
    createImprovements,
    improvements,
    getImprovementsForDropDown,
    commitments,
    getCommitmentsForDropDown,
    openDocument,
    openImage,
    createCase,
    createActionPlanEvent,
    updateActionPlanEvent,
    setState,
    getRiskValue,
    getRiskCategoryFromNumeric,
    calculateNewRR,
    canBeViewed,
    canPreviewDirectly,
    openLocalFile,
    downloadFile,
    getBusinessDays,
    downloadZip,
    getCleanFileName,
    getCompaniesForDropDown,
    companies,
    getIDsForDropDown,
    ids,
    getResponsiblesForDropDown,
    responsibles,
    getControlsForDropDown,
    controls,
    getGapObligationsForDropDown,
    obligations,
  }
}